import { type InventoryStore } from '@/features/inventory/InventoryStore'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import {
  type FilterItem,
  FilterItemSource,
  type FilterStorage,
  FiltersViewModel,
} from '@/infra/FiltersViewModel'
import { action, computed, observable } from 'mobx'
import { tapAction, toStream } from '@/utils/mobx-util'
import { combineLatest, EMPTY, from } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators'
import type { Id } from '@/infra/id'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { shareLatest } from '@/utils/rx-util'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type InventoryItem } from '@/features/inventory/InventoryItem'
import {
  type InventoryItemArchivedFilterOptions,
  type InventoryItemOverrideFilterOptions,
  type InventoryItemProVisibilityFilterOptions,
} from '@/features/inventory/inventory-item-request-query-filter-builder'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { type ActivationLifecycle, activation$ } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { AlertRetryWrapperFactory } from '@/features/shell'

export type RefreshInventoryItems = (workspace: Workspace) => Promise<void>

/**
 * Possible values to filter by Item Type.
 */
export type InventoryItemTypeFilterOption =
  'Active' | 'Overridden' | 'Hidden' | 'Archived'

export interface InventoryItemTypeFilter {
  title: string
  type: InventoryItemTypeFilterOption
  visible: boolean
}

export const InventoryItemTypeFilter = {
  none: {
    title: '',
    type: 'Active',
    visible: false,
  } as InventoryItemTypeFilter,
}

export interface InventoryItemFilter {
  inventoryItemType: InventoryItemTypeFilter
  workspace: Workspace | undefined
}

/**
 * Searches the service items of the selected inventory.
 */
export class InventorySearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Search text.
   */
  readonly searchText = new SearchTextViewModel()

  @observable
  accessor archivedItemsFilter: InventoryItemArchivedFilterOptions = 'Active'

  @observable
  accessor proVisibilityItemsFilter: InventoryItemProVisibilityFilterOptions =
    'All'

  @observable
  accessor overriddenItemsFilter: InventoryItemOverrideFilterOptions = 'All'

  readonly scrollProvider = new InfiniteScrollProvider<InventoryItem>({
    limit: 200,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading service items failed',
        signal,
      )
      return withRetry(() =>
        this._searchInventoryItems(this.workspace?.id, paging, signal),
      )
    },
  })

  /**
   * Inventory filters
   */
  readonly filters

  /**
   * Server query pipeline.
   */
  private readonly _query$

  constructor(
    private readonly _inventoryStore: InventoryStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _filterStorage?: FilterStorage,
  ) {
    this.filters = new FiltersViewModel<InventoryItemFilter>({
      filterStorage: this._filterStorage,
      definitions: {
        inventoryItemType: {
          icon: 'pending',
          title: 'View:',
          implicit: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              InventoryItemTypeFilter.none,
              this.getInventoryItemTypeOptions.bind(this),
            ),
        },
        workspace: {
          icon: 'pending',
          title: 'View:',
          implicit: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              undefined,
              // The workspace filter options are managed externally from the
              // filter view model.
              FilterItemSource.static([]),
            ),
        },
      },
    })

    this._query$ = combineLatest([
      this.filters.appliedFilters$.pipe(
        map((result) => result.workspace?.id),
        distinctUntilChanged(),
      ),
      this.searchText.debouncedValue$,
    ]).pipe(
      switchMap(() =>
        from(
          this.scrollProvider.fetch({ signal: this._lifecycle.signal }),
        ).pipe(
          // The retry alert has reported the failure.
          catchError(() => EMPTY),
        ),
      ),
      shareLatest(),
    )

    this._lifecycle.manage(
      toStream(() => this.filters.appliedFilters.inventoryItemType?.type).pipe(
        tapAction((inventoryItemFilter) =>
          this.updateFilterItems(inventoryItemFilter),
        ),
      ),
    )
    this._lifecycle.manage(
      toStream(() => this.filters.appliedFilters.workspace?.id).pipe(
        tapAction(() => {
          this.searchText.clear()
          this.inventoryTypeFilter.clearValue()
          this.updateFilterItems(this.inventoryTypeFilter.value.type)
        }),
      ),
    )
    // The type options depend on the workspace, so the filter is reactivated
    // to reload them and reselect the first one.
    this._lifecycle.manage(
      this.filters.appliedFilters$.pipe(
        map((result) => result.workspace?.id),
        distinctUntilChanged(),
        switchMap(() => activation$(this.inventoryTypeFilter)),
      ),
    )
    this._lifecycle.manage(this._query$)
    this._lifecycle.manage(this.filters)
  }

  @computed
  get canManageGlobalInventory() {
    return this._getWorkspaceMemberships().some(
      (wms) => wms.member.permissions.adminManageGlobalInventoryItems,
    )
  }

  @computed
  get serviceItems() {
    return this.scrollProvider.items.slice()
  }

  /**
   * Workspace filter
   */
  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspace') as SingleSelectFilter<
      string,
      Workspace | undefined
    >
  }

  /**
   * Inventory type filter
   */
  @computed
  get inventoryTypeFilter() {
    return this.filters.getFilter('inventoryItemType') as SingleSelectFilter<
      string,
      InventoryItemTypeFilter
    >
  }

  /**
   * The workspace whose items are searched, or `undefined` for the global
   * inventory.
   */
  @computed
  get workspace() {
    return this.workspaceFilter.value
  }

  /**
   * Whether we have the Global Inventory option selected or not.
   */
  @computed
  get isGlobalInventorySelected() {
    return this.workspaceFilter.value === undefined
  }

  /**
   * Options for the inventory item type filter.
   */
  getInventoryItemTypeOptions(): Promise<
    FilterItem<InventoryItemTypeFilter>[]
  > {
    return Promise.resolve([
      {
        title: 'Active items',
        value: {
          title: 'Active items',
          type: 'Active',
          visible: true,
        },
      },
      {
        title: 'Overridden items',
        value: {
          title: 'Overridden items',
          type: 'Overridden',
          visible: !this.isGlobalInventorySelected,
        },
      },
      {
        title: 'Hidden items',
        value: {
          title: 'Hidden items',
          type: 'Hidden',
          visible: true,
        },
      },
      {
        title: 'Archived items',
        value: {
          title: 'Archived items',
          type: 'Archived',
          visible: true,
        },
      },
    ])
  }

  /**
   * Sets the archived items filter.
   *
   * @param value
   */
  @action.bound
  setArchivedItemsFilter(value: InventoryItemArchivedFilterOptions) {
    this.archivedItemsFilter = value
  }

  /**
   * Sets the pro visibility items filter.
   *
   * @param value
   */
  @action.bound
  setProVisibilityItemsFilter(value: InventoryItemProVisibilityFilterOptions) {
    this.proVisibilityItemsFilter = value
  }

  /**
   * Sets the overridden items filter.
   *
   * @param value
   */
  @action.bound
  setOverriddenItemsFilter(value: InventoryItemOverrideFilterOptions) {
    this.overriddenItemsFilter = value
  }

  /**
   * Sets the workspace whose items are searched.
   *
   * @param value
   */
  @action.bound
  setWorkspace(value?: Workspace) {
    this.workspaceFilter.change(value)
  }

  /**
   * Re-fetches the items from the first page.
   */
  async refreshItems() {
    // The retry alert has reported a failure, and a newer fetch superseding
    // this one still refreshes the items.
    await this.scrollProvider
      .fetch({ signal: this._lifecycle.signal })
      .catch(() => undefined)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /**
   * Updates the filter items based on the selected filter.
   */
  updateFilterItems(filter?: InventoryItemTypeFilterOption) {
    if (filter === undefined) {
      return
    }

    switch (filter) {
      case 'Active':
        this.setArchivedItemsFilter('Active')
        this.setProVisibilityItemsFilter('All')
        this.setOverriddenItemsFilter('All')
        void this.refreshItems()
        break
      case 'Overridden':
        this.setArchivedItemsFilter('Active')
        this.setProVisibilityItemsFilter('All')
        this.setOverriddenItemsFilter('Only')
        void this.refreshItems()
        break
      case 'Hidden':
        this.setArchivedItemsFilter('Active')
        this.setProVisibilityItemsFilter('Hidden')
        this.setOverriddenItemsFilter('All')
        void this.refreshItems()
        break
      case 'Archived':
        this.setArchivedItemsFilter('Archived')
        this.setProVisibilityItemsFilter('All')
        this.setOverriddenItemsFilter('All')
        void this.refreshItems()
        break
    }
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _searchInventoryItems(
    workspaceId: Id,
    paging: CursorPageQuery,
    signal: AbortSignal,
  ): Promise<CursorPaged<InventoryItem>> {
    if (this.canManageGlobalInventory || workspaceId) {
      return await this._inventoryStore.listInventoryItems(
        {
          ...paging,
          workspaceIds: workspaceId ? [workspaceId] : [],
          searchText: this.searchText.value,
          archivedStatus: this.archivedItemsFilter,
          proVisibilityStatus: this.proVisibilityItemsFilter,
          overrideStatus: this.overriddenItemsFilter,
        },
        signal,
      )
    }
    return CursorPaged.empty()
  }
}
